import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { fmtCVE, fmtDate, QUOTE_STATUS } from "@/lib/format";
import { DocItemsEditor } from "@/components/DocItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Plus, DotsThreeVertical, FileText, CircleNotch, FilePdf, LinkSimple, PaperPlaneTilt, Receipt, PencilSimple, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Quotes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ client_id: "", valid_until: "", payment_terms: "", notes: "", items: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c, p] = await Promise.all([
        api.get("/quotes"), api.get("/clients", { params: { limit: 200 } }),
        api.get("/products", { params: { limit: 200, active: true } }),
      ]);
      setItems(q.data.items); setClients(c.data.items); setProducts(p.data.items);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ client_id: "", valid_until: "", payment_terms: "50% adiantamento, 50% na entrega", notes: "", items: [] });
    setOpen(true);
  };
  const openEdit = async (q) => {
    const { data } = await api.get(`/quotes/${q.id}`);
    setEditing(data);
    setForm({ client_id: data.client_id, valid_until: data.valid_until || "",
      payment_terms: data.payment_terms || "", notes: data.notes || "", items: data.items || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) return toast.error("Selecione um cliente");
    if (form.items.length === 0) return toast.error("Adicione pelo menos uma linha");
    setBusy(true);
    try {
      const payload = { ...form, valid_until: form.valid_until || null };
      if (editing) await api.put(`/quotes/${editing.id}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editing ? "Orçamento atualizado" : "Orçamento criado");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const setStatus = async (q, status) => {
    try { await api.patch(`/quotes/${q.id}/status`, { status }); toast.success("Estado atualizado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const convert = async (q) => {
    try { await api.post(`/quotes/${q.id}/convert`); toast.success("Convertido em pró-forma"); navigate("/vendas"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const copyLink = async (q) => {
    const url = `${window.location.origin}/q/${q.public_token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link público copiado");
  };
  const remove = async (q) => {
    if (!window.confirm(`Eliminar orçamento ${q.number}?`)) return;
    try { await api.delete(`/quotes/${q.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Vendas</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Orçamentos</h1>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="new-quote-btn">
          <Plus size={18} className="mr-1.5" /> Novo Orçamento
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <FileText size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground">Ainda não há orçamentos.</p>
            <Button variant="outline" className="rounded-full" onClick={openNew}>Criar primeiro orçamento</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="hidden md:table-cell">Validade</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((q) => {
                const st = QUOTE_STATUS[q.status] || QUOTE_STATUS.rascunho;
                return (
                  <TableRow key={q.id} className="stagger-item" data-testid={`quote-row-${q.id}`}>
                    <TableCell className="font-mono text-xs font-medium">{q.number}</TableCell>
                    <TableCell>{q.client_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDate(q.valid_until)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtCVE(q.totals?.total)}</TableCell>
                    <TableCell><Badge variant="outline" className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`quote-actions-${q.id}`}>
                            <DotsThreeVertical size={20} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openEdit(q)}><PencilSimple size={15} className="mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`${API}/quotes/${q.id}/pdf`, "_blank")} data-testid={`quote-pdf-${q.id}`}>
                            <FilePdf size={15} className="mr-2" /> Ver PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => copyLink(q)} data-testid={`quote-link-${q.id}`}>
                            <LinkSimple size={15} className="mr-2" /> Copiar link público
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatus(q, "enviado")}>
                            <PaperPlaneTilt size={15} className="mr-2" /> Marcar como enviado
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => convert(q)} data-testid={`quote-convert-${q.id}`}>
                            <Receipt size={15} className="mr-2" /> Converter em pró-forma
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => remove(q)}>
                            <Trash size={15} className="mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader><DialogTitle>{editing ? `Editar ${editing.number}` : "Novo orçamento"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="quote-client"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Válido até</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid" />
              </div>
            </div>
            <DocItemsEditor items={form.items} setItems={(items) => setForm({ ...form, items })} products={products} />
            <div className="space-y-1.5">
              <Label>Condições de pagamento</Label>
              <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="quote-terms" />
            </div>
            <div className="space-y-1.5">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-quote-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
